import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Shield, AtSign, Pencil, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLoading } from "../context/LoadingContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const PERM_LABELS = {
  manage_staff: "Manage Staff", manage_customers: "Manage Customers", manage_events: "Manage Events",
  take_attendance: "Take Attendance", sign_out_sessions: "Sign Out Sessions", suspend_accounts: "Suspend Accounts",
  manage_settings: "Manage Settings", manage_roles: "Manage Roles", manage_notes: "Manage Notes & Warnings",
};

export default function Settings() {
  const [types, setTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [domains, setDomains] = useState([]);
  const [newType, setNewType] = useState({ name: "", color: "#8B5CF6" });
  const [newDomain, setNewDomain] = useState("");
  const [editType, setEditType] = useState(null);
  const [roleDialog, setRoleDialog] = useState(null);
  const { hasPerm } = useAuth();
  const { withLoading } = useLoading();

  const load = async () => {
    const [t, r, p, s] = await Promise.all([api.get("/event-types"), api.get("/roles"), api.get("/permissions"), api.get("/settings")]);
    setTypes(t.data); setRoles(r.data); setAllPerms(p.data.permissions); setDomains(s.data.email_domains);
  };
  useEffect(() => { load(); }, []);

  // event types
  const addType = async () => {
    if (!newType.name) return toast.error("Enter a type name");
    await withLoading(api.post("/event-types", newType), "Adding event type");
    setNewType({ name: "", color: "#8B5CF6" }); toast.success("Event type added"); load();
  };
  const saveType = async () => {
    await withLoading(api.put(`/event-types/${editType.id}`, { name: editType.name, color: editType.color }), "Saving");
    setEditType(null); toast.success("Updated"); load();
  };
  const delType = async (id) => { await withLoading(api.delete(`/event-types/${id}`), "Deleting"); toast.success("Deleted"); load(); };

  // domains
  const addDomain = async () => {
    if (!newDomain) return;
    await withLoading(api.post("/settings/domains", { domain: newDomain }), "Adding domain");
    setNewDomain(""); toast.success("Domain added"); load();
  };
  const delDomain = async (d) => { await withLoading(api.delete(`/settings/domains/${d}`), "Removing"); toast.success("Removed"); load(); };

  // roles
  const openRole = (r) => setRoleDialog(r ? { ...r } : { name: "", color: "#8B5CF6", permissions: [] });
  const saveRole = async () => {
    if (!roleDialog.name) return toast.error("Enter role name");
    const payload = { name: roleDialog.name, color: roleDialog.color, permissions: roleDialog.permissions };
    if (roleDialog.id) await withLoading(api.put(`/roles/${roleDialog.id}`, payload), "Saving role");
    else await withLoading(api.post("/roles", payload), "Creating role");
    setRoleDialog(null); toast.success("Role saved"); load();
  };
  const delRole = async (id) => { await withLoading(api.delete(`/roles/${id}`), "Deleting role"); toast.success("Role deleted"); load(); };
  const togglePerm = (p) => setRoleDialog((rd) => ({ ...rd, permissions: rd.permissions.includes(p) ? rd.permissions.filter((x) => x !== p) : [...rd.permissions, p] }));

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure event types, roles & permissions, and email domains." />
      <Tabs defaultValue="types">
        <TabsList className="bg-slate-900/60">
          <TabsTrigger value="types" data-testid="tab-event-types"><Tag className="mr-2 h-4 w-4" /> Event Types</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="mr-2 h-4 w-4" /> Roles</TabsTrigger>
          <TabsTrigger value="domains" data-testid="tab-domains"><AtSign className="mr-2 h-4 w-4" /> Email Domains</TabsTrigger>
        </TabsList>

        {/* EVENT TYPES */}
        <TabsContent value="types" className="mt-6">
          <div className="card-glass rounded-2xl p-6">
            {hasPerm("manage_settings") && (
              <div className="mb-5 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]"><Label className="text-xs">New Event Type</Label><Input data-testid="new-type-name" value={newType.name} onChange={(e) => setNewType({ ...newType, name: e.target.value })} placeholder="e.g. Ranked Match" className="mt-1 bg-slate-900/60" /></div>
                <input type="color" data-testid="new-type-color" value={newType.color} onChange={(e) => setNewType({ ...newType, color: e.target.value })} className="h-10 w-14 rounded border border-slate-700 bg-transparent" />
                <Button data-testid="add-type-btn" onClick={addType} className="gap-1 bg-purple-600 hover:bg-purple-500"><Plus className="h-4 w-4" /> Add</Button>
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {types.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-xl border border-slate-800/60 bg-slate-900/40 px-4 py-3" data-testid={`type-${t.id}`}>
                  {editType?.id === t.id ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Input value={editType.name} onChange={(e) => setEditType({ ...editType, name: e.target.value })} className="h-8 bg-slate-900/60" />
                      <input type="color" value={editType.color} onChange={(e) => setEditType({ ...editType, color: e.target.value })} className="h-8 w-10 rounded border border-slate-700 bg-transparent" />
                      <Button size="sm" onClick={saveType} className="h-8 bg-emerald-600 hover:bg-emerald-500"><Check className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: t.color }} /><span className="font-medium text-white">{t.name}</span></span>
                      {hasPerm("manage_settings") && (
                        <div className="flex gap-1">
                          <Button data-testid={`edit-type-${t.id}`} size="sm" variant="ghost" onClick={() => setEditType(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button data-testid={`delete-type-${t.id}`} size="sm" variant="ghost" onClick={() => delType(t.id)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ROLES */}
        <TabsContent value="roles" className="mt-6">
          <div className="card-glass rounded-2xl p-6">
            {hasPerm("manage_roles") && <Button data-testid="add-role-btn" onClick={() => openRole(null)} className="mb-5 gap-1 bg-purple-600 hover:bg-purple-500"><Plus className="h-4 w-4" /> New Role</Button>}
            <div className="space-y-3">
              {roles.map((r) => (
                <div key={r.id} className="rounded-xl border border-slate-800/60 bg-slate-900/40 p-4" data-testid={`role-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-semibold" style={{ color: r.color }}><Shield className="h-4 w-4" /> {r.name}</span>
                    {hasPerm("manage_roles") && (
                      <div className="flex gap-1">
                        <Button data-testid={`edit-role-${r.id}`} size="sm" variant="ghost" onClick={() => openRole(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button data-testid={`delete-role-${r.id}`} size="sm" variant="ghost" onClick={() => delRole(r.id)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.permissions.length ? r.permissions.map((p) => <span key={p} className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-300">{PERM_LABELS[p] || p}</span>) : <span className="text-xs text-slate-500">No permissions</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* DOMAINS */}
        <TabsContent value="domains" className="mt-6">
          <div className="card-glass rounded-2xl p-6">
            {hasPerm("manage_settings") && (
              <div className="mb-5 flex items-end gap-3">
                <div className="flex-1"><Label className="text-xs">New Workspace Domain</Label><Input data-testid="new-domain-input" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="ggking.pro" className="mt-1 bg-slate-900/60" /></div>
                <Button data-testid="add-domain-btn" onClick={addDomain} className="gap-1 bg-purple-600 hover:bg-purple-500"><Plus className="h-4 w-4" /> Add</Button>
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {domains.map((d) => (
                <div key={d} className="flex items-center justify-between rounded-xl border border-slate-800/60 bg-slate-900/40 px-4 py-3" data-testid={`domain-${d}`}>
                  <span className="font-mono text-purple-300">@{d}</span>
                  {hasPerm("manage_settings") && <Button data-testid={`delete-domain-${d}`} size="sm" variant="ghost" onClick={() => delDomain(d)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!roleDialog} onOpenChange={(o) => !o && setRoleDialog(null)}>
        <DialogContent className="border-slate-800 bg-[#121620] text-slate-100" data-testid="role-dialog">
          <DialogHeader><DialogTitle>{roleDialog?.id ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
          {roleDialog && (
            <div className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1"><Label className="text-xs">Role Name</Label><Input data-testid="role-name-input" value={roleDialog.name} onChange={(e) => setRoleDialog({ ...roleDialog, name: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
                <input type="color" value={roleDialog.color} onChange={(e) => setRoleDialog({ ...roleDialog, color: e.target.value })} className="h-10 w-14 rounded border border-slate-700 bg-transparent" />
              </div>
              <div>
                <Label className="text-xs">Permissions</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {allPerms.map((p) => (
                    <label key={p} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-800/60 bg-slate-900/40 px-3 py-2 text-sm">
                      <Checkbox data-testid={`perm-${p}`} checked={roleDialog.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} />
                      {PERM_LABELS[p]}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-role-btn" onClick={saveRole} className="bg-purple-600 hover:bg-purple-500">Save Role</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
